import { formatISO } from 'date-fns/formatISO';
import store from 'commons/store/Store';
import { useDialogsStore } from 'commons/stores/dialogs';

/**
 * When the first-time hint on the "Order Samples" entry point is shown, and how "seen" is
 * remembered. See `docs/publishing/sample-order-journey.md`.
 */

/** Must exist in partner-service before this ships — `setSetting` rejects an unknown key. */
export const SAMPLE_ORDER_HINT_SETTING = 'LAST_SAMPLE_ORDER_HINT';

/** Kept apart from the stores so the rule below stays pure. */
export interface SampleOrderHintVisibility {
  /** ISO date of the last time it was shown; empty when never. */
  lastShown: string;
  shownThisSession: boolean;
  /** Whether the partner has already published in the current scope. */
  hasIntent: boolean;
  /** Whether any dialog is open — the sample order journey included. */
  dialogOpen: boolean;
}

/**
 * A date rather than a flag, so "remind again after n days" stays reachable without a new key.
 * `hasIntent` is the inverse of what the onboarding banners on the same page use, which is what
 * keeps the two from competing.
 */
export const shouldShowSampleOrderHint = ({
  lastShown,
  shownThisSession,
  hasIntent,
  dialogOpen,
}: SampleOrderHintVisibility): boolean =>
  !lastShown && !shownThisSession && hasIntent && !dialogOpen;

let shownThisSession = false;

export const sampleOrderHintVisible = (): boolean =>
  shouldShowSampleOrderHint({
    lastShown:
      store.getters['settings/getSetting'](SAMPLE_ORDER_HINT_SETTING) || '',
    shownThisSession,
    hasIntent: !!store.getters['publishingCore/hasIntentForCurrentScope'],
    dialogOpen: useDialogsStore().dialogCount > 0,
  });

/**
 * Written on show, not on dismiss: walking past the hint has to end it too. Admins are skipped as
 * in `versionService` and `Recap`, so the session flag carries them instead.
 */
export const markSampleOrderHintShown = (): void => {
  shownThisSession = true;

  if (store.getters['user/isAdmin']) {
    return;
  }

  // Caught, unlike the rest of PA's `setSetting` calls: a key missing on one platform would
  // otherwise re-show the hint forever with nothing anywhere to say why.
  store
    .dispatch('settings/setSetting', {
      key: SAMPLE_ORDER_HINT_SETTING,
      value: formatISO(new Date()),
    })
    .catch(() => {
      console.warn(
        `[sampleOrder] could not persist ${SAMPLE_ORDER_HINT_SETTING} — is the key registered in partner-service?`,
      );
    });
};
